import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Box,
  Flex,
  Avatar,
  HStack,
  Link,
  IconButton,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  useDisclosure,
  useColorModeValue,
  useBreakpointValue,
  Stack,
} from '@chakra-ui/react';
import { HamburgerIcon, CloseIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { DynivyLogo } from './Logo';
import userIcon from 'assets/user.png';
import { logOut } from '../redux/slices/userSlice';

const MENUS = [
  { name: 'Home', link: '/' },
  // {
  //   name: 'Features',
  //   submenu: [
  //     { name: 'Personalized Writing Coach', link: '/features/feature-1' },
  //     { name: 'Dynamic Writing Prompts', link: '/features/feature-2' },
  //     { name: 'Progress Tracking Reports', link: '/features/feature-3' },
  //     { name: 'Story Writing Games', link: '/features/feature-4' },
  //   ],
  // },
  // { name: 'Tutorial', link: 'tutorial' },
  { name: 'Blog', link: '/blogs' },
  { name: 'Pricing', link: '/pricing' },
  // { name: 'Contact', link: '/contact' },
];

const NavLink = ({ menu }) => {
  if (menu.submenu) {
    // Render the dropdown menu
    return (
      <Menu>
        <MenuButton
          // as={Button}
          variant="ghost"
          px={2}
          py={1}
          rounded={'md'}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          textAlign="left"
          _hover={{
            textDecoration: 'none',
            bg: useColorModeValue('gray.200', 'gray.700'),
          }}
        >
          {menu.name}
          <ChevronDownIcon ml={1} />
        </MenuButton>
        <MenuList>
          {menu.submenu?.map((submenuItem, index) => (
            <MenuItem key={index} as={Link} href={submenuItem.link}>
              {submenuItem.name}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    );
  }

  return (
    <Link
      px={2}
      py={1}
      rounded={'md'}
      _hover={{
        textDecoration: 'none',
        bg: useColorModeValue('gray.200', 'gray.700'),
      }}
      href={menu.link}
    >
      {menu.name}
    </Link>
  );
};

NavLink.propTypes = {
  menu: PropTypes.object.isRequired,
};

const AppHeader = ({ isScrolled }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state.user.isAuthenticated);
  const isAdmin = useSelector((state) => state.user.isAdmin);
  const isMobile = useBreakpointValue({ base: true, md: false });

  const withoutHeaderPaths = ['/writing-practice'];

  for (let path of withoutHeaderPaths) {
    if (location.pathname.startsWith(path)) return null;
  }

  return (
    <Box
      // bg={useColorModeValue('gray.100', 'gray.900')}
      bg="white"
      px={4}
      w="100%"
      position="fixed"
      zIndex={1000}
      // transition="box-shadow 0.3s, border-bottom 0.3s"
      boxShadow={isScrolled ? 'sm' : 'none'} // Shadow appears when scrolled
      borderBottom={isScrolled ? '1px solid #E2E8F0' : 'none'}
    >
      <Flex h={16} alignItems={'center'} justifyContent={'space-between'}>
        <IconButton
          size={'md'}
          icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
          aria-label={'Open Menu'}
          display={{ md: 'none' }}
          onClick={isOpen ? onClose : onOpen}
        />
        <HStack spacing={8} alignItems={'center'}>
          <Box mb={{ base: '2px', md: '6px' }}>
            <DynivyLogo logoType="logoName" />
          </Box>
          <HStack as={'nav'} spacing={4} display={{ base: 'none', md: 'flex' }}>
            {MENUS.map((menu) => (
              <NavLink key={menu.name} menu={menu} />
            ))}
          </HStack>
        </HStack>
        {isAuthenticated ? (
          <Flex alignItems={'center'}>
            <Menu>
              <MenuButton
                as={Button}
                rounded={'full'}
                variant={'link'}
                cursor={'pointer'}
                minW={0}
              >
                <Avatar size={'sm'} src={userIcon} />
              </MenuButton>
              <MenuList>
                {isAdmin && (
                  <MenuItem
                    onClick={() => {
                      navigate('/admin/management/users');
                    }}
                  >
                    Administration
                  </MenuItem>
                )}
                <MenuItem
                  onClick={() => {
                    navigate('/student-profile/assignment');
                  }}
                >
                  My Learning
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    navigate('/settings');
                  }}
                >
                  Settings
                </MenuItem>
                <MenuDivider />
                <MenuItem
                  onClick={() => {
                    dispatch(logOut());
                    navigate('/');
                  }}
                >
                  Log Out
                </MenuItem>
              </MenuList>
            </Menu>
          </Flex>
        ) : (
          <HStack spacing={4}>
            <Button
              as={Link}
              href="/signin"
              variant="ghost"
              color="gray.600"
              fontWeight="medium"
            >
              Log In
            </Button>
            {!isMobile && (
              <Button
                as={Link}
                href="/signup"
                colorScheme="green"
                variant="solid"
              >
                Sign Up
              </Button>
            )}
          </HStack>
        )}
      </Flex>
      {isOpen ? (
        <Box pb={4} display={{ md: 'none' }}>
          <Stack as={'nav'} spacing={4}>
            {MENUS.map((menu) => (
              <NavLink key={menu.name} menu={menu} />
            ))}
          </Stack>
        </Box>
      ) : null}
    </Box>
  );
};

AppHeader.propTypes = {
  isScrolled: PropTypes.string,
};
export default AppHeader;
